import { Stack } from '@chakra-ui/layout';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  InputGroup,
  MenuItem,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import download from 'downloadjs';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiTrash } from 'react-icons/all';
import { Heading, Input, Select, Table } from '~/components';
import {
  useSinister,
  useSinistersInsurersAsSelect,
} from '~/hooks/useSinisters/useSinisters';
import { GeneralInformation } from '~/pages/Portal/Sinisters/GeneralInformation';
import { SinistersNewInsurerForm } from '~/pages/Portal/Sinisters/SinistersPolicy/SinistersNewInsurerForm';
import { getInsuranceLetter } from '~/services/Sinisters';
import apiSinister from '~/utils/apiSinister';
import { queryClient } from '~/utils/react-query';

export const SinistersPolicyForm: React.FC<{
  sinisterId: number;
  link?: boolean;
}> = ({ sinisterId, link = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);

  const { data, isLoading, refetch } = useSinister(sinisterId);
  const [nmr_apolice, setNmrApolice] = useState<string>('');
  const [seguradora, setSeguradora] = useState<number | undefined>(undefined);
  const { data: insurer, isLoading: isInsurerLoading } =
    useSinistersInsurersAsSelect();
  const [carta_positivo, setCartaPosititvo] = useState<
    | {
        nmr_apolice: string;
        seguradora: number;
      }
    | undefined
  >(undefined);
  const [date, setDate] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleOpen = () => {
    if (!data?.contrato_encerrado) {
      setNmrApolice(data?.numero_apolice || '');
    }
    onOpen();
  };

  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setDate('');
    setCartaPosititvo(undefined);
    setNmrApolice('');
    setSeguradora(undefined);
    clearErrors();
  };

  useEffect(() => {
    if (link) handleOpen();
  }, []);

  async function handleCartaPositivo() {
    (!nmr_apolice || !nmr_apolice.toString().trim().length) &&
      setError('nmr_apolice', {
        type: 'required',
        message: 'Número da apólice é obrigatória',
      });

    nmr_apolice.length > 255 &&
      setError('nmr_apolice', {
        type: 'maxLength',
        message: 'Número da apólice com mais de 255 caracteres',
      });

    !seguradora &&
      setError('seguradora', {
        type: 'required',
        message: 'Seguradora é obrigatória',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    if (!nmr_apolice || !seguradora) {
      return;
    }
    setCartaPosititvo({ nmr_apolice, seguradora });
    const newDate = new Date().toLocaleDateString('pt-BR');
    setDate(newDate);
  }

  async function handleCreate() {
    clearErrors();
    setFetching(true);

    const request = apiSinister.put(`/registrar-apolice/${sinisterId}/`, {
      numero_apolice: nmr_apolice,
      seguradora,
    });

    request
      .then(() => {
        toast({
          title: `Apólice registrada`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries('sinistersList');
        queryClient.invalidateQueries(['sinisterLog', sinisterId]);
        queryClient.invalidateQueries(['sinister', sinisterId]);
        queryClient.invalidateQueries('sinisterDashboard');
        queryClient.invalidateQueries(['sinisterDocuments', sinisterId]);
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            toast({
              title: `Erros ao registrar apólice`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao registrar apólice`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  function generateInsuranceLetter(policyNumber: string, insuranceId: number) {
    setIsDownloading(true);
    getInsuranceLetter(sinisterId, insuranceId, policyNumber)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            `CARTA_POSITIVO_${data?.numero_sinistro}`,
            'application/pdf',
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        toast({
          title: 'Erro ao gerar carta',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }

  function handleNmrApolice(e: any) {
    setNmrApolice(e.target.value);
    if (e.target.value.length <= 255) {
      clearErrors('nmr_apolice');
    }
  }

  function handleSeguradora(e: any) {
    setSeguradora(e.target.value);
    if (e.target.options.selectedIndex !== 0) {
      clearErrors('seguradora');
    }
  }

  function handleDelete() {
    setCartaPosititvo(undefined);
  }

  return (
    <>
      <MenuItem onClick={handleOpen}>Registrar apólice</MenuItem>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={!isFetching && !isDownloading}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching || isDownloading} />
          <DrawerHeader>Registrar apólice</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={600} w="100%">
              <Stack gridGap={4}>
                <GeneralInformation sinister={data!} />
                <Heading>Registrar apólice</Heading>
                <HStack gridGap={2}>
                  <FormControl isInvalid={errors.nmr_apolice} width="60%">
                    <FormLabel
                      htmlFor="writeUpFile"
                      style={{ fontSize: '14px' }}
                      justifyContent="space-between"
                      display="flex"
                    >
                      N° da apólice *
                    </FormLabel>
                    <Input
                      name="nmr_apolice"
                      resize="none"
                      error={errors?.nmr_apolice}
                      value={nmr_apolice}
                      onChange={e => handleNmrApolice(e)}
                      fontSize="15px"
                      placeholder="Entre com o número da apólice"
                      isDisabled={
                        isFetching ||
                        carta_positivo !== undefined ||
                        (!!data?.numero_apolice && !data?.contrato_encerrado)
                      }
                    />
                  </FormControl>
                  {data?.contrato_encerrado && (
                    <HStack paddingTop="27px">
                      <Tooltip
                        hasArrow
                        label="Não há número de apólice cadastrada devido o status do contrato estar encerrado."
                        bg="black"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8 0C3.58469 0 0 3.58469 0 8C0 12.4153 3.58469 16 8 16C12.4153 16 16 12.4153 16 8C16 3.58469 12.4153 0 8 0ZM9.46875 8.42044C9.46875 7.60928 8.81119 6.95175 8.00006 6.95175C7.61053 6.95175 7.23688 7.10647 6.96144 7.38194C6.686 7.65738 6.53125 8.03094 6.53125 8.4205V11.5473C6.53125 11.9369 6.686 12.3105 6.96144 12.5859C7.23688 12.8613 7.61047 13.0161 8 13.0161C8.81112 13.0161 9.46875 12.3585 9.46875 11.5474V8.42044ZM8 2.98391C7.18937 2.98391 6.53125 3.64203 6.53125 4.45266C6.53125 5.26328 7.18937 5.92141 8 5.92141C8.81063 5.92141 9.46875 5.26328 9.46875 4.45266C9.46875 3.64203 8.81063 2.98391 8 2.98391Z"
                            fill="#2C2A29"
                          />
                        </svg>
                      </Tooltip>
                    </HStack>
                  )}
                </HStack>
                <FormControl isInvalid={errors.seguradora}>
                  <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                    Seguradora *
                  </FormLabel>
                  <InputGroup width="60%">
                    <Select
                      name="seguradora"
                      placeholder="Selecione"
                      error={errors.seguradora}
                      onChange={e => handleSeguradora(e)}
                      fontSize="15px"
                      value={seguradora}
                      isDisabled={isFetching || carta_positivo !== undefined}
                    >
                      {!isInsurerLoading &&
                        insurer?.map(t => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                    </Select>
                    <SinistersNewInsurerForm />
                  </InputGroup>
                </FormControl>
                <Heading>Carta positivo</Heading>
                <Button
                  type="submit"
                  color="white"
                  background="gray.900"
                  _hover={{
                    bgColor: 'black',
                  }}
                  isDisabled={
                    !(nmr_apolice && seguradora) || carta_positivo !== undefined
                  }
                  width="30%"
                  height={9}
                  onClick={handleSubmit(handleCartaPositivo)}
                >
                  Gerar carta Positivo{' '}
                </Button>
                <Table type="secondary" size="sm" width="60%">
                  <Thead>
                    <Tr>
                      <Th>Data</Th>
                      <Th whiteSpace="nowrap">Carta Positivo gerada</Th>
                      <Th>Ação</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {carta_positivo && (
                      <Tr key={`${1}`}>
                        <Td>{date}</Td>
                        <Td>
                          <Text
                            target="_blank"
                            color="gray.800"
                            _hover={{
                              color: 'gray.900',
                              textDecoration: 'underline',
                            }}
                            cursor="pointer"
                            onClick={() =>
                              generateInsuranceLetter(nmr_apolice, seguradora!)
                            }
                          >
                            Visualizar
                          </Text>
                        </Td>
                        <Td>
                          <Tooltip label="Deletar" placement="top">
                            <IconButton
                              w="25px"
                              fontSize="20px"
                              background="transparent"
                              aria-label="Remove type"
                              icon={<Icon as={FiTrash} />}
                              onClick={() => handleDelete()}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Stack>
            </Stack>
            <Button
              type="submit"
              onClick={handleSubmit(handleCreate)}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
              isDisabled={
                isFetching ||
                !(nmr_apolice && seguradora) ||
                carta_positivo === undefined
              }
            >
              Confirmar{' '}
              {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            <Button
              type="submit"
              onClick={handleClose}
              color="black"
              background="transparent"
              style={{
                marginTop: '20px',
                marginLeft: '10px',
              }}
              isDisabled={isFetching}
            >
              Cancelar
            </Button>
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
